/* eslint-disable quotes */
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField, alpha, useTheme } from "@mui/material";
import { useDebounce } from "../../shared/hooks";
import { useField } from "@unform/core";
import { MunicipiosService } from "../services/api/cadastros/MunicipiosService";
import { styled } from "@mui/material/styles";

type TAutoCompleteOption = {
  idmunicipio: string;
  cidade: string;
  uf: string;
};

interface IAutoCompleteMunicipioProps {
  isExternalLoading?: boolean;
  // Campo do formulário (padrão 'idmunicipio') e rótulo — permite usar o
  // mesmo componente pra mais de um município na tela (ex.: cidade do
  // contador no cadastro da empresa, campo 'contadoridmunicipio').
  name?: string;
  label?: string;
}

// Componente estilizado
const StyledAutocomplete = styled(Autocomplete<TAutoCompleteOption>)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.background.default, 0.8)
      : alpha(theme.palette.common.white, 0.8),
    transition: 'all 0.3s ease',
    
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.05),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },
  
  // Estilo para o popup no modo escuro
  '& .MuiAutocomplete-paper': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.background.paper
      : theme.palette.common.white,
    color: theme.palette.text.primary,
    borderRadius: '12px',
    marginTop: '4px',
    boxShadow: theme.shadows[4],
  },
  
  // Estilo para as opções no modo escuro
  '& .MuiAutocomplete-option': {
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.1),
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.3)
        : alpha(theme.palette.primary.main, 0.2),
    },
  },
}));

export const AutoCompleteMunicipio: React.FC<IAutoCompleteMunicipioProps> = ({
  isExternalLoading = false,
  name = "idmunicipio",
  label = "Município",
}) => {
  const { fieldName, registerField, defaultValue, error, clearError } =
    useField(name);
  const theme = useTheme();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState("");
  const { debounce } = useDebounce();

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      MunicipiosService.getAlllist(busca, selectedId).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          alert(result.message);
        } else {
          setOpcoes(result.data.map((municipio) => municipio));
        }
      });
    });
  }, [busca, selectedId, debounce]);

  const autocompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(
      (opcao) => opcao.idmunicipio === selectedId,
    );
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <StyledAutocomplete
      openText="Abrir"
      closeText="Fechar"
      noOptionsText="Sem opções"
      loadingText="Carregando..."
      disablePortal
      value={autocompleteSelectedOption}
      loading={isLoading}
      disabled={isExternalLoading}
      popupIcon={
        isExternalLoading || isLoading ? (
          <CircularProgress size={28} />
        ) : undefined
      }
      // Sem o filtro por `reason`, o reset automático que o MUI dispara toda vez que `value`
      // muda (pra sincronizar o texto exibido com o rótulo da opção — reason 'reset', não
      // 'input') também virava `busca`. Isso criava um loop: seleciona por id (busca='') →
      // acha 1 opção → MUI reseta o texto pro rótulo completo (ex.: "3550308 - São Paulo -
      // SP") → isso virava `busca` → busca por esse texto não bate com nada → opções ficam
      // vazias → autocomplete desmarca (value=null) → MUI reseta o texto pra '' → busca='' de
      // novo → busca por id de novo → acha a opção de novo → repete pra sempre. Só atualizar
      // `busca` quando for de fato o usuário digitando (reason 'input') quebra o ciclo.
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setBusca(newValue);
      }}
      options={opcoes}
      onChange={(_, newValue) => {
        setSelectedId(newValue?.idmunicipio);
        setBusca("");
        clearError();
      }}
      getOptionLabel={(option) =>
        `${option.idmunicipio} - ${option.cidade} - ${option.uf}`
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          size="small"
          helperText={error}
          defaultValue={defaultValue}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.default, 0.8)
                : alpha(theme.palette.common.white, 0.8),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.light, 0.05),
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.primary.light, 0.08),
              },
            },
          }}
        />
      )}
    />
  );
};